/*----------------------------------
- TLD INSIGHT PAGE
----------------------------------*/

/**
 * The website's `/tld/<slug>` on the marketing layer (batch 4 route 2, the mock website-tld-insight.html): the shared
 * `TldPage` (the ink hero, the kit cards, the rail), then the persona card, the agenda's newsletter band, the related
 * pages, Ask AI and the AI context. No phone action bar (Gaetan, 2026-10-27): the hero's Radar pill and the siblings
 * card's agenda link are the phone's way in.
 *
 * @docs docs/features/tld-intelligence
 * @adr  ADR-0054
 * @adr  ADR-0062
 * @adr  ADR-0110
 * @rule Aggregates, the coarse quality band and one bounded sample only: no score, no popularity, no single sale, no
 *       word about where the sales data comes from, and every TLD link goes to a public-set page or to the agenda
 *       (D31). No sort, filter, search or pager for a visitor. The page body is the shared `TldPage`; this file adds the
 *       website's shell, links, tracking and SEO/GEO tail.
 * @rule The conversion path is frozen: the Radar pill keeps `tld-page-radar-cta`, its wall href (`buildTldRadarHref`),
 *       its attribution and its trigger `website_tld_page_create_radar`; the persona pair keeps its card and triggers.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect, useMemo } from 'preact/hooks';
import {
    TLD_HUB_PATH,
    formatTldLabel,
    tldInsightMarkdownPath,
    tldInsightPath,
    type PublicTldInsight,
    type PublicTldRegistrarPrice,
} from '@uniquedomains/components/contracts/tlds/public';
import { buildPublicSearchConciseMarkdownPath } from '@uniquedomains/core/domains/public-search';
import type { AskYourAIProps } from '@uniquedomains/components/ask-ai';
import Icon from '@uniquedomains/components/kit/icon/Icon';
import { PageMain } from '@uniquedomains/components/marketing/section/PageMain';
import { PageWrap } from '@uniquedomains/components/marketing/section/PageWrap';
import { RelatedPages } from '@uniquedomains/components/marketing/related-pages/RelatedPages';
import TldPage from '@uniquedomains/components/tlds/page/TldPage';
import type { TldPageHeroAction } from '@uniquedomains/components/tlds/page/pageTypes';

import useContext from '@/client/context';
import { MARKETING_ASK_AI_COPY } from '@/client/catalogs/landing/marketingPage';
import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';
import WebsiteAskAi from '@/client/pages/landing/components/ask-ai/WebsiteAskAi';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildDomainsGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import { LANDING_WEBSITE_PAGE_ROUTES } from '@/common/catalogs/landing/publicPages';

import { buildTldAgendaHref } from '../../lib/presentation';
import { TLD_PAGE_ATTRIBUTION, buildTldRadarHref } from '../../lib/tldRadarHref';
import { toTldPageData } from '../../lib/tldPageData';
import { usePublishedTldPaths } from '../../lib/usePublishedTldPaths';
import { useTldGithubIntent } from '../../lib/useTldGithubIntent';
import TldNewsletterBand from '../TldNewsletterBand';
import TldPersonasCard from '../TldPersonasCard';
import TldSampleSection from './TldSampleSection';
import TldSiblingLinks from './TldSiblingLinks';

import '@uniquedomains/components/tlds/page/tldPage.css';
import '../tldClose.css';

/*----------------------------------
- TYPES
----------------------------------*/

type TldInsightPageProps = {
    insight: PublicTldInsight;
    navigation?: LandingHeaderDomainsNavigation | null;
    /** The server's UTC day `YYYY-MM-DD` the page was rendered on, never the browser clock. */
    today: string;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

const TRACKING_PAGE = 'tld_page';
const SUBSCRIBE_ANCHOR_ID = 'subscribe';

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldInsightPage({ insight, navigation, today }: TldInsightPageProps) {
    const content = TLD_PAGE_CONTENT.insight;
    const { slug } = insight;
    const tld = formatTldLabel(slug);
    const pagePath = tldInsightPath(slug);
    const radarHref = buildTldRadarHref(slug);
    const { publicOrigins } = useContext();
    const publishedPaths = usePublishedTldPaths();
    // `cta: 'hub'` is the way back to the hub, from each of the page's three links to it.
    const trackHubLink = () => track('tld_page_cta_click', { cta: 'hub', tld: slug, href: TLD_HUB_PATH });
    // One trigger for every Radar entry; the section names the entry (the hero pill, `tld_radar`).
    const trackRadar = (section: 'tld_radar') => {
        trackConversionTrigger('website_tld_page_create_radar', { href: radarHref, page: TRACKING_PAGE, section });
        track('tld_page_cta_click', { cta: 'radar', tld: slug, href: radarHref });
    };
    const trackGithub = (href: string) => {
        trackConversionTrigger('website_tld_page_github_link', { href, page: TRACKING_PAGE, section: 'tld_github' });
        track('github_repo_open_attempt', { section: 'tld_github', target: 'tld_repository' });
        track('tld_page_cta_click', { cta: 'github', tld: slug, href });
    };
    const trackRegistrar = (registrar: PublicTldRegistrarPrice, href: string) => {
        trackConversionTrigger('website_tld_page_registrar', { href, page: TRACKING_PAGE, section: 'tld_costs' });
        track('tld_page_cta_click', { cta: 'registrar', tld: slug, registrar: registrar.registrar, href });
    };
    useTldGithubIntent(slug, insight.githubRepoUrl);

    useEffect(() => {
        track('page_view', { page: TRACKING_PAGE, path: window.location.pathname, tld: slug });
    }, [slug]);

    const geoContext = useMemo(
        () =>
            buildDomainsGeoContext({
                currentPath: pagePath,
                fullMarkdownMirrorPath: tldInsightMarkdownPath(slug),
                hasSearchFilters: false,
                markdownMirrorPath: buildPublicSearchConciseMarkdownPath('tld', slug),
                previewRows: [],
                tld: slug,
            }),
        [pagePath, slug],
    );
    const askAiContext = useMemo<AskYourAIProps>(
        () => ({
            pageType: 'tld',
            pageUrl: new URL(pagePath, publicOrigins.websiteOrigin).toString(),
            pageTitle: content.meta.pageTitle(tld),
            tld: slug,
            geoContext,
            previewRows: insight.sample.map((row) => ({
                name: row.domain,
                tld: slug,
                status: row.status,
                ...(row.price !== null ? { pricePurchase: row.price } : {}),
            })),
        }),
        [content.meta, geoContext, insight.sample, pagePath, publicOrigins.websiteOrigin, slug, tld],
    );
    const { githubRepoUrl } = insight;
    const heroActions: TldPageHeroAction[] = [
        {
            id: 'radar',
            label: content.hero.radarOn(tld),
            href: radarHref,
            tone: 'cta',
            onClick: () => trackRadar('tld_radar'),
            testId: 'tld-page-radar-cta',
            attribution: TLD_PAGE_ATTRIBUTION,
        },
        ...(githubRepoUrl
            ? [{
                id: 'dataset',
                label: content.hero.getDataset,
                href: githubRepoUrl,
                tone: 'on-ink' as const,
                external: true,
                onClick: () => trackGithub(githubRepoUrl),
                testId: 'tld-page-github-link',
            }]
            : []),
    ];
    const relatedLinks = [
        { label: content.related.hub, href: TLD_HUB_PATH, onClick: trackHubLink },
        { label: content.agendaLink(tld), href: buildTldAgendaHref(slug) },
        { label: content.related.radars, href: LANDING_WEBSITE_PAGE_ROUTES.productRadars },
    ];
    return (
        <>
            {/* The page reserves the header pill itself (`wp-main`'s top padding), so the header draws no spacer. */}
            <Header spacer={false} links={LANDING_WEBSITE_LINKS} navigation={navigation ?? EMPTY_LANDING_NAVIGATION} currentPath={pagePath} trackingPage={TRACKING_PAGE} />

            <PageMain id="top" data-testid="tld-page" data-tld={slug}>
                {/* `tl-frame`: on a phone the page takes the hero card's gutters (`--card-side`, 10px under 900px), as the
                    mock's `.wp-main > .wrap` rule and the registrar heroes (mock parity R6-3, T-5): the page gutter
                    (22px at 390) left the dark hero 12px short of every other hero. The closing wrap carries the same rule. */}
                <PageWrap class="tl-frame">
                    <TldPage
                        testIdPrefix="tld-page"
                        today={today}
                        copy={content.page}
                        data={toTldPageData(insight)}
                        hrefs={{ back: TLD_HUB_PATH }}
                        heroActions={heroActions}
                        onBackClick={trackHubLink}
                        onRegistrarClick={trackRegistrar}
                        slots={{
                            // Under "Market size" (ADR-0062 amendment of 2026-10-05): the ten-name sample, which names
                            // no sale.
                            afterMarket: <TldSampleSection slug={slug} sample={insight.sample} />,
                            bottomRight: (
                                <TldSiblingLinks
                                    slug={slug}
                                    relatedSlugs={insight.relatedSlugs}
                                    publishedPaths={publishedPaths}
                                    apiUrl={insight.apiUrl}
                                    onHubClick={trackHubLink}
                                />
                            ),
                        }}
                    />
                </PageWrap>

                <PageWrap class="tl-close tl-close--page">
                    <TldPersonasCard
                        testId="tld-page-conversion"
                        attribution={TLD_PAGE_ATTRIBUTION}
                        investorTriggerId="website_tld_page_investor"
                        founderTriggerId="website_tld_page_founder"
                        trackingPage={TRACKING_PAGE}
                        section="tld_page_personas"
                        surface="tld_page"
                    />

                    <TldNewsletterBand
                        anchorId={SUBSCRIBE_ANCHOR_ID}
                        source="website_tld_page"
                        onSubscribed={() => track('tld_page_cta_click', { cta: 'newsletter', tld: slug })}
                    />

                    <RelatedPages label={content.related.title} links={relatedLinks} arrow={<Icon name="arrow-right" />} />

                    <WebsiteAskAi title={MARKETING_ASK_AI_COPY.title} context={askAiContext} providerTitle={MARKETING_ASK_AI_COPY.providerTitle} />

                    <PageGeoBlock geo={geoContext} surface="tld_insight" />
                </PageWrap>
            </PageMain>

            <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage={TRACKING_PAGE} />
        </>
    );
}
